/**
 * Ad-hoc demo "view as" user registry.
 *
 * Lets the UI quickly switch the visible user persona (profile, domain
 * agents, projects, dataset types) without involving the agents-core
 * backend. The agents-core seed only knows about the `dot` user; this
 * file is purely a frontend convenience for demos.
 *
 * To add a user: append a new entry. To add a CEC scenario file: drop
 * the .xlsx under `datasets/cec/<subfolder>/` and add a matching entry
 * under the cec user's `projects[0].types`.
 */

export interface DemoDatasetType {
  key: string;
  label: string;
}

export interface DemoProject {
  key: string;
  label: string;
  types: DemoDatasetType[];
}

export interface DemoAgent {
  key: string;
  display_name: string;
  description: string;
}

export interface DemoUser {
  key: string;            // stable id (e.g. 'doe_tto')
  label: string;          // dropdown label (e.g. 'DOE TTO')
  username: string;       // backend username for /api/me (when applicable)
  display_name: string;
  profile: { name: string; description: string };
  agents: DemoAgent[];
  projects: DemoProject[];
}

export const DEMO_USERS: DemoUser[] = [
  {
    key: 'doe_tto',
    label: 'DOE TTO',
    username: 'dot',
    display_name: 'DOE TTO Program Manager',
    profile: {
      name: 'doe_tto',
      description: 'DOE Technology Transitions Office program manager',
    },
    agents: [
      {
        key: 'ev_charging_analyst',
        display_name: 'Load and Infrastructure Specialist',
        description: 'NCI / TEIS charging-event and load analysis',
      },
      {
        key: 'large_load_interconnection_specialist',
        display_name: 'Large Load Interconnection Specialist',
        description: 'Placeholder — not yet trained',
      },
      {
        key: 'critical_mineral_specialist',
        display_name: 'Critical Mineral Specialist',
        description: 'Placeholder — not yet trained',
      },
    ],
    projects: [
      {
        key: 'nci',
        label: 'NCI',
        types: [
          { key: 'charging_events', label: 'Charging Events' },
          { key: 'load_profiles', label: 'Load Profiles' },
          { key: 'chargers', label: 'Chargers' },
          { key: 'simulation_results', label: 'Simulation Results' },
        ],
      },
      {
        key: 'teis',
        label: 'TEIS',
        types: [
          { key: 'charging_sessions', label: 'Charging Sessions' },
        ],
      },
    ],
  },
  {
    key: 'cec',
    label: 'CEC',
    username: 'cec',
    display_name: 'CEC Analyst',
    profile: {
      name: 'cec',
      description: 'California Energy Commission analyst',
    },
    agents: [
      {
        key: 'ev_charging_analyst',
        display_name: 'MHD Charger Load Specialist',
        description: 'Census-tract MHD charger load analysis',
      },
    ],
    projects: [
      {
        key: 'cec',
        label: 'CEC',
        // Mirrors the .xlsx files under datasets/cec/. Add new entries
        // here when new scenarios are dropped in.
        types: [
          {
            key: 'mhd_charger_low_cap_2027',
            label: 'MHD Charger — Low Cap 2027',
          },
          {
            key: 'mhd_charger_base_cap_2027',
            label: 'MHD Charger — Base Cap 2027',
          },
          {
            key: 'mhd_charger_low_cap_2030',
            label: 'MHD Charger — Low Cap 2030',
          },
          {
            key: 'mhd_charger_base_cap_2030',
            label: 'MHD Charger — Base Cap 2030',
          },
        ],
      },
    ],
  },
];

export const DEFAULT_VIEW_AS_KEY = 'doe_tto';

export function getDemoUser(key: string): DemoUser {
  return DEMO_USERS.find((u) => u.key === key) ?? DEMO_USERS[0];
}
